/* ============================================================================
 * src/utils/commonFunctions/VEBEnrollment/coreToVEBPlan.ts
 * ----------------------------------------------------------------------------
 * Reshapes a core plan (same_as_veb_structure) into the shape CommonForm and
 * dynamicPremiumCalculation already expect. Nothing in CommonForm or
 * PremimumCalculation.ts changes.
 * ========================================================================== */

/* eslint-disable */

const SPOUSE_RELATIONSHIPS = ["SPOUSE"];

/**
 * Core stores dependent_eligibility as one row per relationship; VEB stores a
 * single object with spouse / children blocks. getCommonEligibleDependents and
 * EligiblityDialog both read the VEB shape.
 *
 * Note: VEB carries ONE age range for all child relationship types, so the
 * child rows collapse onto the first one. That round-trips today because the
 * create API writes the same children values to every child row.
 */
const toVEBDependentEligibility = (rows: any) => {
	if (!Array.isArray(rows) || rows.length === 0) return null;

	const spouseRow = rows.find((row) => SPOUSE_RELATIONSHIPS.includes(String(row?.relationship_type).toUpperCase()));
	const childRow = rows.find((row) => row !== spouseRow);

	return {
		is_present: rows.some((row) => row?.is_present !== false),
		is_visible_in_form: rows.some((row) => !!row?.is_visible_in_form),

		eligible_relationship: rows.filter((row) => row?.is_eligible !== false).map((row) => row.relationship_type),
		in_eligible_relationship: rows.filter((row) => row?.is_eligible === false).map((row) => row.relationship_type),

		spouse_relationship_has_own_si_limit: !!spouseRow?.has_own_si_limit,
		child_relationship_has_own_si_limit: !!childRow?.has_own_si_limit,

		spouse: {
			premium_depends_on_age: !!spouseRow?.premium_depends_on_age,
			min_age: spouseRow?.min_age ?? 18,
			max_age: spouseRow?.max_age ?? 100,
			can_spouse_benefit_exceed_employee: !!spouseRow?.can_benefit_exceed_employee
		},

		children: {
			premium_depends_on_age: !!childRow?.premium_depends_on_age,
			min_age: childRow?.min_age ?? 0,
			max_age: childRow?.max_age ?? 26,
			can_child_benefit_exceed_employee: !!childRow?.can_benefit_exceed_employee,
			each_child_has_own_benefit: !!childRow?.each_member_has_own_benefit,
			disabled: {
				min_age: childRow?.disabled?.min_age ?? 0,
				max_age: childRow?.disabled?.max_age ?? 100
			}
		}
	};
};

/** Core: [{ plan_name, plan_code, is_taken_together }] → VEB: [{ plan, required, same_coverage }] */
const toVEBPlanDependency = (dependencies: any) =>
	(Array.isArray(dependencies) ? dependencies : []).map((dependency: any) => ({
		plan: dependency?.plan_name,
		required: !!dependency?.is_taken_together,
		same_coverage: false
	}));

export const toVEBPlanShape = (plan: any) => {
	if (!plan) return plan;

	const {
		coverage_for,
		coverage_volume,
		benefit_amount,
		age_criteria,
		dependent_eligibility,
		plan_dependencies,
		pay_frequency,
		...rest
	} = plan;

	return {
		...rest,

		coverage_for: coverage_for ?? { options: [], is_present: false, is_visible_in_form: false },

		coverage_volume: {
			...(coverage_volume ?? { options: [] }),
			// Core's schema defaults coverage_volume to is_present:true. A plan with
			// no volume would put that level into the signature while the premium
			// table has no such level, and every lookup would fail one step in.
			is_present: !!coverage_volume?.is_present && (coverage_volume?.options?.length ?? 0) > 0
		},

		coverage_volume_same_as_employee: !!rest.coverage_volume_same_as_employee,

		// Core has no pay_frequency field. is_present must be true — every case in
		// getStructuredPremiumArray ends on it. The actual frequency used for the
		// lookup comes from the member's paycheck, not from here.
		pay_frequency: {
			options: pay_frequency?.options ?? ["WEEKLY", "BIWEEKLY", "MONTHLY"],
			is_visible_in_form: pay_frequency?.is_visible_in_form ?? true,
			is_present: true
		},

		benefit_amount: benefit_amount ?? null,
		age_criteria: age_criteria ?? null,

		// shape fixes below this line; order no longer matters
		dependent_eligibility: toVEBDependentEligibility(dependent_eligibility),

		is_this_plan_has_riders: !!(plan.rider?.is_present || plan.rider?.name),

		standard_plan_dependency: rest.standard_plan_dependency ?? toVEBPlanDependency(plan_dependencies),
		rider_plan_dependency: rest.rider_plan_dependency ?? [],

		// Core drops these at save time; false is correct for plans created today.
		// See the note in the chat — if a core plan ever needs tobacco rating,
		// these have to be added to the schema, not defaulted here.
		is_premium_depends_on_smoke: !!rest.is_premium_depends_on_smoke,
		is_premium_depnends_on_spouse_smoke: !!rest.is_premium_depnends_on_spouse_smoke,
		is_premium_depnends_on_child_smoke: !!rest.is_premium_depnends_on_child_smoke
	};
};
